import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import useStyles from './styles'
import NewPassword from './components/NewPassword'
import { useParams } from 'react-router-dom'
import AuthService from '@/services/AuthService'
import { useWhiteLabelStore } from '@/store'
import { TimeZoneGMT } from '@/components/TimeZoneGMT'
import { Loader, Text } from '@conexasaude/conexa-hero'
import { RenderIf } from '@/components/RenderIf'
import { useIsFirstRender } from '@/hooks/useIsFirstRender'
import { setCache } from '@/utils/cache'
import { showErrorToast } from '@/utils/requisitionErrors'
import { openWhatsappSupport } from '@/utils/openWhatsappSupport'
import { useRebrandedThemeGenerator } from '@/store/ThemeGeneratorStore/rebrandedTheme.store'
import logAnalyticsEvent from '@/Firebase/analytics'

type PasswordChangingFormParams = {
  uniqueCode?: string
  token: string
}

const PasswordChangingForm: FunctionComponent = () => {
  const styles = useStyles()
  const { uniqueCode, token } = useParams<PasswordChangingFormParams>()
  const setUniqueCode = useWhiteLabelStore(store => store.setUniqueCode)
  const setRebrandedTheme = useRebrandedThemeGenerator(
    store => store.setRebrandedTheme,
  )
  const [isLoading, setIsLoading] = useState(false)
  const isFirstRender = useIsFirstRender()

  const handleParams = useCallback(async () => {
    if (!uniqueCode) return
    try {
      setIsLoading(true)
      const clinicRebrandedColors =
        await AuthService.getClinicRebrandedColorsByUniqueCode(uniqueCode)

      setRebrandedTheme(clinicRebrandedColors)
      setUniqueCode(uniqueCode)
    } catch (err) {
      setRebrandedTheme(null)

      setUniqueCode(null)
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }, [setRebrandedTheme, setUniqueCode, uniqueCode])

  const getExternalToken = useCallback(async () => {
    if (!token) return
    try {
      const response = await AuthService.getUserByToken(token)
      setCache('externalToken', response.token)
    } catch (err) {
      showErrorToast(err)
    }
  }, [token])

  useEffect(() => {
    if (!isFirstRender) return
    handleParams()
    getExternalToken()
  }, [getExternalToken, handleParams, isFirstRender])

  const openSupportAndSendEvents = () => {
    logAnalyticsEvent('Pac_Ativacao_ConversarSuporte')
    openWhatsappSupport()
  }

  return (
    <div>
      <RenderIf condition={!!uniqueCode && isLoading}>
        <div css={styles.loadingScreen}>
          <Text renderAs="h3" variant="paragraphLarge">
            Redirecionando...
          </Text>
          <Loader size="large" />
        </div>
      </RenderIf>
      <RenderIf condition={!uniqueCode || !isLoading}>
        <div css={styles.container}>
          <div css={styles.mainCard}>
            <NewPassword />
          </div>
          <div css={styles.footer}>
            <Text
              variant="linkUnderline"
              css={styles.linkSupport}
              onClick={openSupportAndSendEvents}
            >
              Conversar com o Suporte
            </Text>
            <TimeZoneGMT />
            <Text renderAs="span" variant="microCopy">
              © 2021 Conexa Saúde · Todos os direitos reservados.
            </Text>
          </div>
        </div>
      </RenderIf>
    </div>
  )
}

export default PasswordChangingForm
